import { useEffect, useId, useRef, useState, type RefObject } from "react";

import { cn } from "../../lib/cn";
import { SearchIcon } from "../icons";

/** One thing a CommandDialog offers to do. */
export interface Command<Id extends string = string> {
	/** Unique among the dialog's commands: what `onChoose` is handed. */
	readonly id: Id;
	/** What it does, the way a button would say it: "Add vendor". Its option's accessible name. */
	readonly label: string;
	/** One quieter line under the label, saying what it is for. */
	readonly description: string;
	/** The heading it is listed under. A group's commands are listed together, the groups in the order they come. */
	readonly group: string;
	/** A word or two at the end of the row saying where it happens: "Directory". */
	readonly hint: string;
	/** The 7 px dot before the label, as a full background class ("bg-finance"), so Tailwind can find it. */
	readonly dotClassName: string;
	/** Other words the search box finds it by, such as "supplier" for Add vendor. */
	readonly keywords: readonly string[];
}

export interface CommandDialogProps<Id extends string> {
	readonly open: boolean;
	/** The dialog's accessible name: "New". */
	readonly title: string;
	/** What the search box asks while it is empty; its accessible name too. */
	readonly placeholder: string;
	/** Everything the dialog offers, in the order it lists them. */
	readonly commands: readonly Command<Id>[];
	/** Called with the command chosen, by Enter or by a click. The dialog does not close itself: the caller does. */
	readonly onChoose: (id: Id) => void;
	/**
	 * Called whenever the dialog closes: Escape, a backdrop click (in browsers that support `closedby`) or `open`
	 * turning false — for Escape and a backdrop click as soon as they ask, before the dialog's close event, which the
	 * browser only fires a task later. It can run twice, and after the parent already closed it, so make it idempotent.
	 */
	readonly onClose: () => void;
}

/**
 * Whether a command answers to everything the search holds: each of its words has to appear in the command's label,
 * description, group, hint or keywords, in any case. An empty search keeps every command.
 */
function answers(command: Command, search: string): boolean {
	const text = [command.label, command.description, command.group, command.hint, ...command.keywords]
		.join(" ")
		.toLowerCase();
	return search
		.toLowerCase()
		.split(/\s+/)
		.every(word => text.includes(word));
}

/** Commands under their groups, each group where its first command came and holding its commands in their order. */
function groupsOf<Id extends string>(
	commands: readonly Command<Id>[],
): { readonly name: string; readonly commands: readonly Command<Id>[] }[] {
	const names = [...new Set(commands.map(command => command.group))];
	return names.map(name => ({ name, commands: commands.filter(command => command.group === name) }));
}

interface CommandSearchProps<Id extends string> {
	readonly placeholder: string;
	readonly commands: readonly Command<Id>[];
	readonly onChoose: (id: Id) => void;
	/** The search box, which the dialog focuses as it opens. */
	readonly searchRef: RefObject<HTMLInputElement | null>;
}

/**
 * The inside of an open dialog: the search box and the commands answering it. It is only there while the dialog is
 * open, so every opening starts from an empty search with the first command highlighted.
 */
function CommandSearch<Id extends string>({ placeholder, commands, onChoose, searchRef }: CommandSearchProps<Id>) {
	const [search, setSearch] = useState("");
	// Which of the commands found is highlighted, counted in the order they are listed.
	const [highlight, setHighlight] = useState(0);
	const baseId = useId();
	const listboxId = `${baseId}-commands`;
	const found = groupsOf(commands.filter(command => answers(command, search)));
	const listed = found.flatMap(group => group.commands);
	const highlighted = listed.at(Math.min(highlight, listed.length - 1));
	// Each command's id is its place among all of them: not its own id, which could hold a space an id reference cannot,
	// and not its place in the list found, which a narrower list gives another command — a screen reader told the same
	// active option again announces nothing.
	const optionId = (command: Command<Id>) => `${baseId}-option-${String(commands.indexOf(command))}`;
	const listboxRef = useRef<HTMLDivElement>(null);

	// A highlight moved by the arrows onto a command the list has scrolled out of sight scrolls it back into view. jsdom
	// has no layout and no scrollIntoView.
	useEffect(() => {
		const option = listboxRef.current?.querySelector('[aria-selected="true"]');
		if (option instanceof HTMLElement && "scrollIntoView" in option) {
			option.scrollIntoView({ block: "nearest" });
		}
	}, [highlighted]);

	return (
		<div className="flex max-h-[calc(100dvh-15vh-2rem)] flex-col">
			<div className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 py-3">
				<SearchIcon
					className="h-3.5 w-3.5 shrink-0 fill-none stroke-ink-3 stroke-[1.8]"
					strokeLinecap="round"
				/>
				<input
					ref={searchRef}
					role="combobox"
					aria-label={placeholder}
					aria-autocomplete="list"
					aria-expanded={highlighted !== undefined}
					aria-controls={listboxId}
					aria-activedescendant={highlighted === undefined ? undefined : optionId(highlighted)}
					placeholder={placeholder}
					autoComplete="off"
					spellCheck={false}
					value={search}
					onChange={event => {
						setSearch(event.target.value);
						setHighlight(0);
					}}
					onKeyDown={event => {
						// A key that finishes an input method's composition is the input method's, not a choice.
						if (event.nativeEvent.isComposing) {
							return;
						}
						if (event.key === "ArrowDown" || event.key === "ArrowUp") {
							// The arrows walk the commands found, stopping at either end, and leave the caret alone.
							event.preventDefault();
							const at = Math.min(highlight, listed.length - 1);
							setHighlight(
								event.key === "ArrowDown" ? Math.min(at + 1, listed.length - 1) : Math.max(at - 1, 0),
							);
						} else if (event.key === "Enter") {
							event.preventDefault();
							if (highlighted !== undefined) {
								onChoose(highlighted.id);
							}
						}
					}}
					className="min-w-0 flex-1 border-0 bg-transparent font-sans text-body text-ink outline-none placeholder:text-ink-3"
				/>
			</div>
			{/* Always there, so that a screen reader hears it when a search leaves nothing; empty and unseen otherwise. */}
			<p
				role="status"
				className={cn(
					"text-center text-caption text-ink-3",
					highlighted === undefined ? "px-4 py-6" : "sr-only",
				)}
			>
				{highlighted === undefined ? `Nothing matches “${search.trim()}”.` : ""}
			</p>
			{/* Always there too, hidden while it lists nothing: the search box's aria-controls names it. */}
			<div
				ref={listboxRef}
				id={listboxId}
				role="listbox"
				aria-label="Commands"
				hidden={highlighted === undefined}
				className="thin-scroll min-h-0 flex-1 overflow-y-auto py-1.5"
			>
				{found.map((group, index) => (
					<div key={group.name} role="presentation">
						<div
							id={`${baseId}-group-${String(index)}`}
							aria-hidden="true"
							className="px-4 pt-2.5 pb-1 text-label font-semibold tracking-caps text-ink-3 uppercase"
						>
							{group.name}
						</div>
						<div role="group" aria-labelledby={`${baseId}-group-${String(index)}`}>
							{group.commands.map(command => {
								const isHighlighted = command === highlighted;
								return (
									// eslint-disable-next-line jsx-a11y-x/click-events-have-key-events -- the keys are the search box's, which keeps focus and points at the highlight (aria-activedescendant)
									<div
										key={command.id}
										id={optionId(command)}
										role="option"
										tabIndex={-1}
										aria-selected={isHighlighted}
										aria-labelledby={`${optionId(command)}-label`}
										aria-describedby={`${optionId(command)}-description ${optionId(command)}-hint`}
										// Keeps focus in the search box, where typing and the arrows go on working.
										onMouseDown={event => {
											event.preventDefault();
										}}
										onPointerMove={() => {
											if (!isHighlighted) {
												setHighlight(listed.indexOf(command));
											}
										}}
										onClick={() => {
											onChoose(command.id);
										}}
										className={cn(
											"flex cursor-pointer items-start gap-2.5 px-4 py-2.5 transition-colors duration-[120ms] select-none",
											// The Settings navigation's current page: plain in either theme, where the hover tint is not.
											isHighlighted && "bg-accent/10 ring-1 ring-accent/25 ring-inset",
										)}
									>
										<span
											aria-hidden="true"
											className={cn(
												"mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full",
												command.dotClassName,
											)}
										/>
										<span className="min-w-0 flex-1">
											<span
												id={`${optionId(command)}-label`}
												className="block truncate text-ctl-lg font-medium text-ink"
											>
												{command.label}
											</span>
											<span
												id={`${optionId(command)}-description`}
												className="block truncate text-caption text-ink-3"
											>
												{command.description}
											</span>
										</span>
										<span
											id={`${optionId(command)}-hint`}
											className="shrink-0 text-caption text-ink-3"
										>
											{command.hint}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
			<div
				aria-hidden="true"
				className="flex shrink-0 items-center gap-3.5 border-t border-line px-4 py-2 text-micro text-ink-3"
			>
				<span>↑↓ to move</span>
				<span>↵ to choose</span>
				<span>esc to close</span>
			</div>
		</div>
	);
}

/**
 * A modal of commands with a search box over them: typing narrows the list, the arrows move the highlight, Enter or a
 * click chooses. Built on the native modal <dialog>, like the Drawer and the ConfirmDialog, so the browser traps focus,
 * closes it on Escape, returns focus to the opener and paints it in the top layer. It sits 15% down the window, centred
 * across it, so it keeps its place as the list narrows. The search box is an ARIA combobox that keeps focus while the
 * highlight moves through the listbox under it.
 */
export function CommandDialog<Id extends string>({
	open,
	title,
	placeholder,
	commands,
	onChoose,
	onClose,
}: CommandDialogProps<Id>) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		if (open && !dialog.open) {
			dialog.showModal();
			// The browser focuses the search box itself, the dialog's first control; jsdom's stand-in does not.
			searchRef.current?.focus();
		} else if (!open && dialog.open) {
			dialog.close();
		}
	}, [open]);

	return (
		<dialog
			ref={dialogRef}
			aria-label={title}
			closedby="any"
			// The browser asks with cancel and closes the dialog there and then, but fires close a task later: a ⌘K
			// pressed in between must already find it closed, or it would take the dialog for open and close it again.
			onCancel={onClose}
			onClose={() => {
				// And a close fired after the dialog has opened again, from that task, is the last closing's, not this one.
				if (dialogRef.current?.open !== true) {
					onClose();
				}
			}}
			className="card-face m-auto mt-[15vh] w-[min(560px,92vw)] max-w-none rounded-card border border-line p-0 text-ink backdrop:bg-[rgba(26,26,24,.25)]"
		>
			{open ? (
				<CommandSearch
					placeholder={placeholder}
					commands={commands}
					onChoose={onChoose}
					searchRef={searchRef}
				/>
			) : null}
		</dialog>
	);
}
